import { Injectable } from '@angular/core';
import {
  ActiveLookup,
  Lookup,
  LookupKeys,
  Lookups,
} from '@ag-common-lib/public-api';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { LookupsManagerService } from './lookups-manager.service';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { QueryParam, WhereFilterOperandKeys } from '../dao/CommonFireStoreDao.dao';
import { getShirtSizeSortWeight } from "../utils/t-shirt-size.utils";
import { orderBy } from 'lodash';
import { SortDescriptor } from 'devextreme/data';

@Injectable({ providedIn: 'root' })
export class LookupsDefaultService {
  readonly statesLookup$: Observable<ActiveLookup[]>;
  readonly mediaTagsLookup$: Observable<ActiveLookup[]>;
  readonly gendersLookup$: Observable<ActiveLookup[]>;
  readonly dietaryConsiderationTypesLookup$: Observable<ActiveLookup[]>;
  readonly tShortSizesLookup$: Observable<ActiveLookup[]>;
  readonly tShirtAdultLookup$: Observable<ActiveLookup[]>;
  readonly tShirtYouthLookup$: Observable<ActiveLookup[]>;
  readonly shirtMenLookup$: Observable<ActiveLookup[]>;
  readonly shirtWomenLookup$: Observable<ActiveLookup[]>;
  readonly suffixesLookup$: Observable<ActiveLookup[]>;
  readonly prefixesLookup$: Observable<ActiveLookup[]>;
  readonly taskCategoryLookup$: Observable<ActiveLookup[]>;
  readonly taskSubcategoryLookup$: Observable<ActiveLookup[]>;
  readonly associationTypeLookup$: Observable<ActiveLookup[]>;
  readonly emailTypeLookup$: Observable<ActiveLookup[]>;
  readonly supportTypesLookup$: Observable<ActiveLookup[]>;
  readonly agencyStatusesLookup$: Observable<ActiveLookup[]>;
  readonly agencyOrganizationAffiliations$: Observable<ActiveLookup[]>;
  readonly agencyPhoneTypes$: Observable<ActiveLookup[]>;
  readonly agencyEmailTypes$: Observable<ActiveLookup[]>;
  readonly agencyAddressTypesLookup$: Observable<ActiveLookup[]>;
  readonly agencySocialTypesLookup$: Observable<ActiveLookup[]>;
  readonly agencySocialMediaLookup$: Observable<ActiveLookup[]>;
  readonly agencyWebsiteTypesLookup$: Observable<ActiveLookup[]>;
  readonly socialTypesLookup$: Observable<ActiveLookup[]>;
  readonly socialMediaLookup$: Observable<ActiveLookup[]>;
  readonly websiteTypesLookup$: Observable<ActiveLookup[]>;

  private readonly orderedSuffixes = ['jr', 'sr', 'ii', 'iii', 'iiii', 'iv'];
  private readonly orderedAssociationsTypes = [
    'spouse',
    'business partner',
    'friend',
    'father',
    'mother',
    'brother',
    'sister',
    'son',
    'daughter',
    'grandparent',
    'family member',
  ];

  constructor(private lookupsManagerService: LookupsManagerService) {
    const createLookup$ = (lookup: Lookups): Observable<ActiveLookup[]> =>
      combineLatest([
        this.lookupsManagerService.getList(lookup),
        this.lookupsManagerService.getDefaultSorting(lookup),
      ]).pipe(
        map(([items, sortDescriptor]) => this.normalizeLookup(items, lookup, sortDescriptor)),
        catchError(error => {
          console.warn(`Failed to load lookup "${lookup}":`, error);
          // On permission or any other Firestore error, fall back to an empty list
          return of<ActiveLookup[]>([]);
        }),
        shareReplay(1),
      );

    this.gendersLookup$ = createLookup$(Lookups.Genders);
    this.prefixesLookup$ = createLookup$(Lookups.Prefixes);
    this.statesLookup$ = createLookup$(Lookups.States);
    this.mediaTagsLookup$ = createLookup$(Lookups.MediaTags);
    this.taskCategoryLookup$ = createLookup$(Lookups.TaskCategory);
    this.taskSubcategoryLookup$ = createLookup$(Lookups.TaskSubcategory);
    this.dietaryConsiderationTypesLookup$ = createLookup$(Lookups.DietaryConsiderationType);
    this.emailTypeLookup$ = createLookup$(Lookups.EmailTypes);
    this.tShortSizesLookup$ = createLookup$(Lookups.TShirtSize);
    this.tShirtAdultLookup$ = createLookup$(Lookups.TShirtAdult);
    this.tShirtYouthLookup$ = createLookup$(Lookups.TShirtYouth);
    this.shirtMenLookup$ = createLookup$(Lookups.ShirtMen);
    this.shirtWomenLookup$ = createLookup$(Lookups.ShirtWomen);
    this.supportTypesLookup$ = createLookup$(Lookups.SupportTypes);
    this.agencyStatusesLookup$ = createLookup$(Lookups.AgencyStatuses);
    this.agencyOrganizationAffiliations$ = createLookup$(Lookups.AgencyOrganizationAffiliations);
    this.agencyPhoneTypes$ = createLookup$(Lookups.AgencyPhoneTypes);
    this.agencyEmailTypes$ = createLookup$(Lookups.AgencyEmailTypes);
    this.agencyAddressTypesLookup$ = createLookup$(Lookups.AgencyAddressTypes);
    this.socialTypesLookup$ = createLookup$(Lookups.SocialTypes);
    this.socialMediaLookup$ = createLookup$(Lookups.SocialMedia);
    this.websiteTypesLookup$ = createLookup$(Lookups.WebsiteTypes);
    this.agencySocialTypesLookup$ = createLookup$(Lookups.AgencySocialTypes);
    this.agencySocialMediaLookup$ = createLookup$(Lookups.AgencySocialMedia);
    this.agencyWebsiteTypesLookup$ = createLookup$(Lookups.AgencyWebsiteTypes);

    this.suffixesLookup$ = this.lookupsManagerService.getList(Lookups.Suffixes).pipe(
      map(this.normalizeLookupWithoutSorting),
      map(items => items.sort(this.comparator.bind(this, this.orderedSuffixes))),
      shareReplay(1),
    );
    this.associationTypeLookup$ = this.lookupsManagerService.getList(Lookups.AssociationType).pipe(
      map(this.normalizeLookupWithoutSorting),
      map(items => items.sort(this.comparator.bind(this, this.orderedAssociationsTypes))),
      shareReplay(1),
    );
  }

  public getLookupByName = (lookup: Lookups): Observable<Partial<ActiveLookup>[]> => {
    switch (lookup) {
      case Lookups.States:
        return this.statesLookup$;
      case Lookups.EmailTypes:
        return this.emailTypeLookup$;
      case Lookups.Genders:
        return this.gendersLookup$;
      case Lookups.Suffixes:
        return this.suffixesLookup$;
      case Lookups.Prefixes:
        return this.prefixesLookup$;
      case Lookups.TaskCategory:
        return this.taskCategoryLookup$;
      case Lookups.TaskSubcategory:
        return this.taskSubcategoryLookup$;
      case Lookups.AssociationType:
        return this.associationTypeLookup$;
      case Lookups.TShirtSize:
        return this.tShortSizesLookup$;
      case Lookups.TShirtAdult:
        return this.tShirtAdultLookup$;
      case Lookups.TShirtYouth:
        return this.tShirtYouthLookup$;
      case Lookups.ShirtMen:
        return this.shirtMenLookup$;
      case Lookups.ShirtWomen:
        return this.shirtWomenLookup$;
      case Lookups.DietaryConsiderationType:
        return this.dietaryConsiderationTypesLookup$;
      case Lookups.SupportTypes:
        return this.supportTypesLookup$;
      case Lookups.AgencyStatuses:
        return this.agencyStatusesLookup$;
      case Lookups.AgencyOrganizationAffiliations:
        return this.agencyOrganizationAffiliations$;
      case Lookups.AgencyPhoneTypes:
        return this.agencyPhoneTypes$;
      case Lookups.AgencyEmailTypes:
        return this.agencyEmailTypes$;
      case Lookups.SocialTypes:
        return this.socialTypesLookup$;
      case Lookups.SocialMedia:
        return this.socialMediaLookup$;
      case Lookups.WebsiteTypes:
        return this.websiteTypesLookup$;
      case Lookups.AgencySocialTypes:
        return this.agencySocialTypesLookup$;
      case Lookups.AgencySocialMedia:
        return this.agencySocialMediaLookup$;
      case Lookups.AgencyWebsiteTypes:
        return this.agencyWebsiteTypesLookup$;
      default:
        return of([]);
    }
  };

  getTaskSubcategoryLookup = taskCategoryDbId => {
    if (!taskCategoryDbId) {
      return undefined;
    }

    return combineLatest([
      this.lookupsManagerService.getList(Lookups.TaskSubcategory, [
        new QueryParam(LookupKeys.dependsOn, WhereFilterOperandKeys.equal, taskCategoryDbId),
      ]),
      this.lookupsManagerService.getDefaultSorting(Lookups.TaskSubcategory),
    ]).pipe(
      map(([items, sortDescriptor]) => this.normalizeLookup(items, Lookups.TaskSubcategory, sortDescriptor)),
      catchError(error => {
        console.warn('Failed to load TaskSubcategory lookup:', error);
        return of<ActiveLookup[]>([]);
      }),
      shareReplay(1),
    );
  };

  private normalizeLookupWithoutSorting = (items: Lookup[]): ActiveLookup[] => {
    return Array.isArray(items)
      ? items.map(lookup => {
          const {
            [BaseModelKeys.dbId]: dbId,
            [BaseModelKeys.firebaseRef]: reference,
            [LookupKeys.value]: value,
            [LookupKeys.description]: description,
            [LookupKeys.isActive]: isActive,
            [LookupKeys.isDefault]: isDefault,
            [LookupKeys.isAssigned]: isAssigned,
            [LookupKeys.orderBy]: orderBy,
          } = lookup;

          return {
            [BaseModelKeys.dbId]: dbId,
            [LookupKeys.value]: value,
            [LookupKeys.reference]: reference,
            [LookupKeys.description]: description,
            [LookupKeys.isAssigned]: isAssigned,
            [LookupKeys.isDefault]: isDefault,
            [LookupKeys.visible]: isActive,
            [LookupKeys.orderBy]: orderBy,
          };
        })
      : [];
  };

  private normalizeLookup = (
    items: Lookup[],
    lookup: Lookups,
    sortDescriptor?: SortDescriptor<string>[],
  ): ActiveLookup[] => {
    const normalizedList = this.normalizeLookupWithoutSorting(items);

    if (!sortDescriptor || !Array.isArray(sortDescriptor) || sortDescriptor.length === 0) {
      // default sorting
      const defaultSortingKey = lookup === Lookups.States ? LookupKeys.value : LookupKeys.description;
      return orderBy(normalizedList, [defaultSortingKey], ['asc']);
    }

    return orderBy(normalizedList, [LookupKeys.orderBy], ['asc']);
  };

  /**
   * Comparator for clothing sizes using universal sort weights.
   * @param left First lookup item
   * @param right Second lookup item
   */
  private sizeComparator = (left: ActiveLookup, right: ActiveLookup): number => {
    const weightLeft = getShirtSizeSortWeight(String(left?.value ?? ''));
    const weightRight = getShirtSizeSortWeight(String(right?.value ?? ''));

    if (typeof weightLeft === 'number' && typeof weightRight === 'number') {
      return weightLeft - weightRight;
    }

    return String(weightLeft).localeCompare(String(weightRight));
  };

  private comparator = (items: string[], left: ActiveLookup, right: ActiveLookup) => {
    const valuesMap = this.getMapping(items);

    const getSortWeight = (lookup: ActiveLookup) => {
      const val = `${lookup?.value}`.toLowerCase();
      if (valuesMap.has(val)) {
        return valuesMap.get(val);
      }
      return items.findIndex(item => val.includes(item)) === -1
        ? `${valuesMap.size} ${val}`
        : items.findIndex(item => val.includes(item)).toString();
    };

    const leftValue = getSortWeight(left);
    const rightValue = getSortWeight(right);

    return leftValue.localeCompare(rightValue, 'en', {
      numeric: true,
      sensitivity: 'base',
      ignorePunctuation: true,
    });
  };

  private getMapping = (items: string[]) => {
    return new Map(items.map((item, index) => [item, index.toString()]));
  };
}
